import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type {SxProps} from '@mui/system';
import dayjs from 'dayjs';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import CalendarPickerEditableOnHover from '@/components/common/forms/CalendarPickerEditableOnHover';
import Title from '@/components/common/Title';
import MapDetail from '@/components/ForestMassDetail/MapDetail';
import SpeciesLegend from '@/components/ForestMassDetail/SpeciesLegend';
import InventoryPlotInfo from '@/components/InventoryPlotDetail/InventoryPlotInfo';
import InventoryPlotRealWeight from '@/components/InventoryPlotDetail/InventoryPlotRealWeight';
import InventoryPlotTotalVolumes from '@/components/InventoryPlotDetail/InventoryPlotTotalVolumes';
import InventoryStemTable from '@/components/InventoryPlotDetail/InventoryStemTable';
import SlamPointCloudButton from '@/components/PointCloudsUploadForm/slam';
import type {ForestMass} from '@/domain/entities/ForestMass';
import type {ForestMassSpecies} from '@/domain/entities/ForestMassSpecies';
import type {InventoryPlot} from '@/domain/entities/InventoryPlot';
import type {InventoryStem} from '@/domain/entities/InventoryStem';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {SlamPointCloud, SlamPointCloudCreate} from '@/domain/entities/SlamPointCloud';
import type {Species} from '@/domain/entities/Species';
import type UserPermissions from '@/domain/useCases/userPermissions';

//TYPES
type Props = {
  forestMass: ForestMass;
  inventoryPlots: Array<InventoryPlot>;
  forestMassSpecies?: Array<ForestMassSpecies>;
  species: Array<Species>;
  selectedInventoryPlot: InventoryPlot;
  inventoryStems: Array<InventoryStem>;
  slamPointCloud: SlamPointCloud | undefined;
  lidarTechnologies: Array<LidarTechnology>;
  permissions: UserPermissions;
  onBack: () => void;
  onInventoryPlotSelect: (uuid: string) => void;
  onUploadSlamPointCloud: (pointCloud: Partial<SlamPointCloudCreate>, file: File) => void;
  onDeleteSlamPointCloud: (uuid: string) => void;
  onDownloadSlamPointCloud: (uuid: string) => void;
  onReuploadSlamPointCloud: (uuid: string, file: File) => void;
  onAppraiserWeightChange: (appraiserWeight: number) => void;
};

const InventoryPlotDetail: FC<Props> = ({
  forestMass,
  inventoryPlots,
  forestMassSpecies,
  species,
  lidarTechnologies,
  selectedInventoryPlot,
  inventoryStems,
  slamPointCloud,
  permissions,
  onBack,
  onInventoryPlotSelect,
  onUploadSlamPointCloud,
  onDeleteSlamPointCloud,
  onAppraiserWeightChange,
  onDownloadSlamPointCloud,
  onReuploadSlamPointCloud,
}) => {
  const {t} = useTranslation();

  const {boundary} = forestMass;
  const {code, uuid, dateOfSurvey} = selectedInventoryPlot;

  const canUpload = permissions.hasPermissionToUploadSlamCloudPoints();
  const canDelete = permissions.hasPermissionToDeleteSlamCloudPoints();

  return (
    <Stack direction="column" sx={styles.container}>
      <Title onBack={onBack} label={t('inventoryPlots.detailTitle')} />
      <Stack sx={styles.content}>
        <Paper sx={styles.detail} elevation={0}>
          <Stack direction="row" spacing={1} sx={{justifyContent: 'space-between'}}>
            <Stack direction="row" spacing={2} sx={{alignItems: 'flex-end', justifyContent: 'flex-start'}}>
              <Typography variant="h6" sx={{fontWeight: 700, pb: 0.5}}>
                {code}
              </Typography>
              {dateOfSurvey && (
                <CalendarPickerEditableOnHover
                  editable={false}
                  value={forestMass.plannedCuttingDate || dayjs()}
                  onChange={() => {}}
                />
              )}
            </Stack>
            <SlamPointCloudButton
              canUpload={canUpload}
              canDelete={canDelete}
              slamPointCloud={slamPointCloud}
              lidarTechnologies={lidarTechnologies}
              onDownload={onDownloadSlamPointCloud}
              onDelete={onDeleteSlamPointCloud}
              onUpload={onUploadSlamPointCloud}
              onReupload={onReuploadSlamPointCloud}
            />
          </Stack>

          <Stack sx={styles.plotInfo}>
            <InventoryPlotInfo
              species={species}
              inventoryPlot={selectedInventoryPlot}
              forestMassSpecies={forestMassSpecies}
            />
          </Stack>

          <Stack direction="row" spacing={4} sx={styles.realWeightRow}>
            <Stack sx={styles.realWeight}>
              <InventoryPlotRealWeight
                permissions={permissions}
                inventoryPlot={selectedInventoryPlot}
                onAppraiserWeightChange={onAppraiserWeightChange}
              />
            </Stack>
            <Stack sx={styles.totalVolumes}>
              <InventoryPlotTotalVolumes inventoryPlot={selectedInventoryPlot} />
            </Stack>
          </Stack>

          <Stack sx={styles.table}>
            <InventoryStemTable inventoryStems={inventoryStems} />
          </Stack>
        </Paper>

        <Paper sx={styles.map}>
          <MapDetail
            species={species}
            forestMassSpecies={forestMassSpecies}
            boundary={boundary}
            inventoryPlots={inventoryPlots}
            onInventoryPlotClick={onInventoryPlotSelect}
            selectedInventoryPlotUuid={uuid}
          />
          <SpeciesLegend species={species} />
        </Paper>
      </Stack>
    </Stack>
  );
};
export default InventoryPlotDetail;

const styles: Record<string, SxProps> = {
  container: {
    height: '100%',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      lg: '2fr 1fr',
    },
    gridTemplateRows: {
      xs: 'auto 320px',
      lg: '1fr',
    },
    gap: 2,
    width: '100%',
    alignItems: 'start',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  detail: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    height: '100%',
    minHeight: {
      xs: 500,
      lg: 'unset',
    },
    p: 2,
    pl: 3,
    minWidth: 0,
    maxWidth: '100%',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  plotInfo: {
    flexShrink: 0,
    mb: 4,
  },
  realWeightRow: {
    flexShrink: 0,
    mb: 4,
  },
  realWeight: {
    width: '320px',
    flexShrink: 0,
  },
  totalVolumes: {
    flexShrink: 0,
  },
  map: {
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    p: 2,
    height: {
      xs: 320,
      sm: 400,
      lg: '100%',
    },
    minHeight: {
      lg: 500,
    },
  },
  divider: {
    my: 1,
  },
};
